import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import {LoadingStates} from '@Utils/loading.util';
import {
  closeAiActionLoadingModal,
  isCurrentModalSession,
  openPublishingLoadingModalAndGetSessionId,
  showPublishingReadyModal,
} from '@Components/ai-chat-window/lib/ai-action-loading-modal.library';
import {getPosterHashedIdForAiChatImageAsset} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {getEventWizardURLWithDesignPrefill} from '@Libraries/events.library';
import {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {isUserPAYG} from '@Libraries/user.library';
import {openUpsellingDialogForEmailCampaigns} from '@Libraries/email-campaigns-library';
import {useAiChatActions} from '@AiChat/providers/ai-chat.context';

interface UsePublishChatModelGeneratedImage {
  publishToSocialMedia: () => Promise<void>;
  promoteAsEvent: () => Promise<void>;
  publishAsEmailCampaign: () => Promise<void>;
}

export const usePublishChatModelGeneratedImage = (chatId: string, hashedId: string): UsePublishChatModelGeneratedImage => {
  const actions = useAiChatActions();

  const updateLoadingPublishingOption = (loadingOption: PublishingOption | null, loadingState: LoadingStates): void => {
    actions.updateLoadingStatesForImageActions(chatId, hashedId, {
      loadingPublishingOption: loadingOption,
      publishingLoadingState: loadingState,
    });
  };

  const handlePublishToSocialMedia = async (): Promise<void> => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_PUBLISH, {[GA4EventParamName.TYPE]: 'SocialMedia'});
    updateLoadingPublishingOption(PublishingOption.PUBLISH_TO_SOCIAL_MEDIA, LoadingStates.LOADING);
    const sessionId = openPublishingLoadingModalAndGetSessionId(PublishingOption.PUBLISH_TO_SOCIAL_MEDIA);

    try {
      const posterHashedId = await getPosterHashedIdForAiChatImageAsset(hashedId);
      const url = window.PMW.util.site_url(`socialmedia/post/design/${posterHashedId}`);
      updateLoadingPublishingOption(PublishingOption.PUBLISH_TO_SOCIAL_MEDIA, LoadingStates.LOADED);

      if (isCurrentModalSession(sessionId)) {
        showPublishingReadyModal(PublishingOption.PUBLISH_TO_SOCIAL_MEDIA, {url, openInNewTab: true});
      }
    } catch (e: unknown) {
      console.error(e);
      updateLoadingPublishingOption(null, LoadingStates.NOT_LOADED);

      if (isCurrentModalSession(sessionId)) {
        closeAiActionLoadingModal();
      }
    }
  };

  const handlePromoteAsEvent = async (): Promise<void> => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_PUBLISH, {[GA4EventParamName.TYPE]: 'Event'});
    updateLoadingPublishingOption(PublishingOption.PROMOTE_AS_EVENT, LoadingStates.LOADING);
    const sessionId = openPublishingLoadingModalAndGetSessionId(PublishingOption.PROMOTE_AS_EVENT);

    try {
      const posterHashedId = await getPosterHashedIdForAiChatImageAsset(hashedId);
      const url = getEventWizardURLWithDesignPrefill(posterHashedId, MediaTypes.MY_DESIGNS);
      updateLoadingPublishingOption(PublishingOption.PROMOTE_AS_EVENT, LoadingStates.LOADED);

      if (isCurrentModalSession(sessionId)) {
        showPublishingReadyModal(PublishingOption.PROMOTE_AS_EVENT, {url, openInNewTab: true});
      }
    } catch (e: unknown) {
      console.error(e);
      updateLoadingPublishingOption(null, LoadingStates.NOT_LOADED);

      if (isCurrentModalSession(sessionId)) {
        closeAiActionLoadingModal();
      }
    }
  };

  const handlePublishAsEmailCampaign = async (): Promise<void> => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_PUBLISH, {[GA4EventParamName.TYPE]: 'Email'});

    if (isUserPAYG()) {
      openUpsellingDialogForEmailCampaigns();
      return;
    }

    updateLoadingPublishingOption(PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN, LoadingStates.LOADING);
    const sessionId = openPublishingLoadingModalAndGetSessionId(PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN);

    try {
      const posterHashedId = await getPosterHashedIdForAiChatImageAsset(hashedId);
      const url = window.PMW.util.site_url(`emailmarketing/publish/design/${posterHashedId}`);
      updateLoadingPublishingOption(PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN, LoadingStates.LOADED);

      if (isCurrentModalSession(sessionId)) {
        showPublishingReadyModal(PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN, {url, openInNewTab: true});
      }
    } catch (e: unknown) {
      console.error(e);
      updateLoadingPublishingOption(null, LoadingStates.NOT_LOADED);

      if (isCurrentModalSession(sessionId)) {
        closeAiActionLoadingModal();
      }
    }
  };

  return {
    promoteAsEvent: handlePromoteAsEvent,
    publishAsEmailCampaign: handlePublishAsEmailCampaign,
    publishToSocialMedia: handlePublishToSocialMedia,
  };
};
